import type { ClusterRow, ClusterSort } from '@radar/contracts';
import { queryOptions } from '@tanstack/react-query';
import { api, apiUrl, call } from '@/lib/api';
import { type ClustersListFilters, type KeywordsListFilters, qk } from '@/lib/query-keys';
import type { ClustersSearch } from '@/lib/search-schemas';
import { JobResponse, type MergeInput, type PromoteInput, PromoteResponse } from './schemas';

// Accès à `/api/v1/clusters` et `/api/v1/keywords` (`api.md` § Mots-clés et clusters). Les routes de
// lecture passent par le client typé ; les mutations (`promote` T22, `merge` et `dismiss` T19,
// `reanalyze` T25) ne sont pas encore dans `AppType` : `fetch` brut sur le contrat d'`api.md`,
// réponse validée par zod. À basculer sur `call(api.clusters[':id'].promote.$post(...))` quand
// ces routes existent.

export type ClustersListData = Awaited<ReturnType<typeof listClusters>>;
export type ClusterDetailData = Awaited<ReturnType<typeof getCluster>>;
export type KeywordsListData = Awaited<ReturnType<typeof listKeywords>>;

/** Taille de page par valeur quand un filtre multi-valeurs force plusieurs requêtes. */
export const CLUSTERS_MULTI_PAGE_SIZE = 200;
export const CLUSTER_KEYWORDS_PAGE_SIZE = 50;
export const MERGE_SEARCH_PAGE_SIZE = 10;

/**
 * Coût indicatif d'une analyse profonde (`T17` : ~5 000 tokens d'entrée, 1 500 de sortie), affiché
 * dans le dialogue de ré-analyse (`patterns.md` § 15) tant que le serveur n'expose pas d'estimation.
 */
export const DEEP_ANALYSIS_ESTIMATE_USD = 0.025;

/**
 * Search params de `/clusters` → requêtes `GET /clusters`. Le contrat T14 n'accepte qu'une valeur
 * par filtre `stage` / `eligibility` : plusieurs valeurs produisent une requête par combinaison
 * (page 1, 200 lignes), fusionnées et paginées côté client (`hooks.ts`).
 */
export function toClustersQueries(search: ClustersSearch, marketId: string): ClustersListFilters[] {
  const stages = search.stage && search.stage.length > 0 ? search.stage : [undefined];
  const eligibilities =
    search.eligibility && search.eligibility.length > 0 ? search.eligibility : [undefined];
  const multi = stages.length * eligibilities.length > 1;
  const base: ClustersListFilters = {
    page: multi ? '1' : String(search.page),
    pageSize: multi ? String(CLUSTERS_MULTI_PAGE_SIZE) : String(search.pageSize),
    sort: search.sort,
    order: search.order,
    marketId,
    ...(search.minPriority === undefined ? {} : { minPriority: String(search.minPriority) }),
    ...(search.hasOpportunity === undefined
      ? {}
      : { hasOpportunity: String(search.hasOpportunity) }),
    ...(search.q ? { q: search.q } : {}),
  };
  const queries: ClustersListFilters[] = [];
  for (const stage of stages) {
    for (const eligibility of eligibilities) {
      queries.push({
        ...base,
        ...(stage ? { stage } : {}),
        ...(eligibility ? { eligibility } : {}),
      });
    }
  }
  return queries;
}

function sortValue(row: ClusterRow, sort: ClusterSort): number | string | null {
  switch (sort) {
    case 'priorityScore':
      return row.priorityScore;
    case 'triageScore':
      return row.triageScore;
    case 'volumeSum':
      return row.metrics?.volume_sum ?? null;
    case 'updatedAt':
      return row.updatedAt;
  }
}

/** Fusion de plusieurs pages (filtre multi-valeurs) : dédoublonnage, tri serveur reproduit (nulls en dernier en `desc`). */
export function mergeClusterRows(
  lists: readonly (readonly ClusterRow[])[],
  sort: ClusterSort,
  order: 'asc' | 'desc',
): ClusterRow[] {
  const byId = new Map<string, ClusterRow>();
  for (const list of lists) for (const row of list) byId.set(row.id, row);
  const direction = order === 'asc' ? 1 : -1;
  return Array.from(byId.values()).sort((a, b) => {
    const va = sortValue(a, sort);
    const vb = sortValue(b, sort);
    // Comme le serveur : `NULLS LAST` en `desc`, `NULLS FIRST` en `asc`.
    if (va === null && vb === null) return a.id.localeCompare(b.id);
    if (va === null) return -direction;
    if (vb === null) return direction;
    const cmp =
      typeof va === 'string' && typeof vb === 'string'
        ? va.localeCompare(vb)
        : Number(va) - Number(vb);
    return cmp !== 0 ? cmp * direction : a.id.localeCompare(b.id);
  });
}

export function listClusters(query: ClustersListFilters) {
  return call(api.clusters.$get({ query }));
}

export const clustersListOptions = (query: ClustersListFilters) =>
  queryOptions({ queryKey: qk.clusters.list(query), queryFn: () => listClusters(query) });

export function getCluster(id: string) {
  return call(api.clusters[':id'].$get({ param: { id } }));
}

export const clusterDetailOptions = (id: string) =>
  queryOptions({ queryKey: qk.clusters.detail(id), queryFn: () => getCluster(id) });

export function listKeywords(query: KeywordsListFilters) {
  return call(api.keywords.$get({ query }));
}

export const keywordsListOptions = (query: KeywordsListFilters) =>
  queryOptions({ queryKey: qk.keywords.list(query), queryFn: () => listKeywords(query) });

/** Candidats de fusion : clusters du marché (10 premiers par volume), l'appelant exclut l'id courant. */
export const mergeCandidatesQuery = (marketId: string, q: string): ClustersListFilters => ({
  marketId,
  pageSize: String(MERGE_SEARCH_PAGE_SIZE),
  page: '1',
  sort: 'volumeSum',
  order: 'desc',
  ...(q ? { q } : {}),
});

async function postJson(path: string, body?: unknown): Promise<unknown> {
  return call<unknown>(
    fetch(apiUrl(path), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    }),
  );
}

/** `POST /clusters/:id/promote` (route T22) → `{ id }` de l'opportunité créée. */
export async function promoteCluster(id: string, input: PromoteInput): Promise<PromoteResponse> {
  return PromoteResponse.parse(
    await postJson(`/clusters/${encodeURIComponent(id)}/promote`, input),
  );
}

/** `POST /clusters/:id/merge` (route T19). */
export async function mergeCluster(id: string, input: MergeInput): Promise<void> {
  await postJson(`/clusters/${encodeURIComponent(id)}/merge`, input);
}

/** `POST /clusters/:id/reanalyze` (route T25) → `202 { jobId }`. */
export async function reanalyzeCluster(id: string): Promise<JobResponse> {
  return JobResponse.parse(await postJson(`/clusters/${encodeURIComponent(id)}/reanalyze`));
}

/** `POST /clusters/merge-suggestions/:id/dismiss` (route T19). */
export async function dismissMergeSuggestion(id: string): Promise<void> {
  await postJson(`/clusters/merge-suggestions/${encodeURIComponent(id)}/dismiss`);
}
